
import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getAssessmentBySlug } from '@/services/assessmentService';
import { Assessment } from '@/types/assessment';
import FreeAssessment from './FreeAssessment';
import PremiumAssessment from './PremiumAssessment';
import { useToast } from '@/hooks/use-toast';
import Logo from '@/icons/Logo';
import PremiumAuthPage from '@/components/premium-auth/PremiumAuthPage';
import PremiumDashboard from '@/components/premium-dashboard/PremiumDashboard';
import PremiumResumeDialog from '@/components/premium-dashboard/PremiumResumeDialog';
import { supabase } from '@/integrations/supabase/client';
import { loadPremiumProgress, deletePremiumProgress } from '@/services/premiumProgressService';

const hasOngoingAssessment = (assessmentId: string): boolean => {
  try {
    const stored = localStorage.getItem(`assessment_${assessmentId}_responses`);
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return Object.keys(parsed).length > 0;
  } catch {
    return false;
  }
};

const clearAssessmentProgress = (assessmentId: string) => {
  localStorage.removeItem(`assessment_${assessmentId}_responses`);
  localStorage.removeItem(`assessment-progress-${assessmentId}`);
  localStorage.removeItem(`assessment-progress-premium-${assessmentId}`);
  // Also clear from DB
  deletePremiumProgress(assessmentId).catch(() => {});
};

const AssessmentPortal = () => {
  const { slug = '', mode = '' } = useParams();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [premiumAuthenticated, setPremiumAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(mode === 'premium');
  const [premiumView, setPremiumView] = useState<'dashboard' | 'assessment'>('dashboard');
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [startFresh, setStartFresh] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [pendingAssessment, setPendingAssessment] = useState<Assessment | null>(null);
  const { toast } = useToast();

  // Check if user is already authenticated for premium mode
  useEffect(() => {
    if (mode !== 'premium') {
      setCheckingAuth(false);
      return;
    }

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: orgMember } = await supabase
          .from('organization_members')
          .select('id')
          .eq('id', session.user.id)
          .maybeSingle();

        if (orgMember) {
          setPremiumAuthenticated(true);
        }
      }
      setCheckingAuth(false);
    };

    checkSession();
  }, [mode]);

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const data = await getAssessmentBySlug(slug);
        
        if (!data) {
          setError('Assessment not found');
          toast({
            variant: "destructive",
            title: "Error",
            description: "The requested assessment could not be found."
          });
          return;
        }
        
        if (!data.isActive) {
          setError('Assessment is not active');
          toast({
            variant: "destructive",
            title: "Assessment Unavailable",
            description: "This assessment is currently not active."
          });
          return;
        }
        
        setAssessment(data);
        document.title = `Gapstars | ${data.title}`;
      } catch (err) {
        setError('Failed to load assessment');
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load the assessment. Please try again later."
        });
        console.error('Error fetching assessment:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [slug, toast]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (premiumView === 'assessment') {
        setPremiumView('dashboard');
        setSelectedAssessment(null);
      } else if (premiumAuthenticated && !e.state?.view) {
        setPremiumAuthenticated(false);
        supabase.auth.signOut();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [premiumView, premiumAuthenticated]);

  // Handle starting a premium assessment from the dashboard
  const handleStartPremiumAssessment = async (assessmentId: string, assessmentSlug: string) => {
    // Check localStorage first, then DB for ongoing progress
    let hasProgress = hasOngoingAssessment(assessmentId);
    
    if (!hasProgress) {
      // Check DB for persisted progress
      try {
        const dbProgress = await loadPremiumProgress(assessmentId);
        if (dbProgress && dbProgress.responses && Object.keys(dbProgress.responses).length > 0) {
          hasProgress = true;
          // Restore to localStorage so the assessment can use it
          localStorage.setItem(`assessment_${assessmentId}_responses`, JSON.stringify(dbProgress.responses));
        }
      } catch (err) {
        console.warn('Could not check DB progress:', err);
      }
    }

    if (hasProgress) {
      try {
        const data = await getAssessmentBySlug(assessmentSlug);
        if (data) {
          setPendingAssessment(data);
          setResumeDialogOpen(true);
        } else {
          toast({ variant: "destructive", title: "Error", description: "Could not load the selected assessment." });
        }
      } catch {
        toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
      }
      return;
    }

    setLoadingSelected(true);
    try {
      const data = await getAssessmentBySlug(assessmentSlug);
      if (data) {
        launchAssessment(data, true);
      } else {
        toast({ variant: "destructive", title: "Error", description: "Could not load the selected assessment." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
    } finally {
      setLoadingSelected(false);
    }
  };

  const launchAssessment = (data: Assessment, fresh: boolean) => {
    if (fresh) {
      clearAssessmentProgress(data.id);
    }
    setStartFresh(fresh);
    setSelectedAssessment(data);
    setPremiumView('assessment');
    window.history.pushState({ view: 'assessment' }, '');
  };

  const handleResumeChoice = (resume: boolean) => {
    setResumeDialogOpen(false);
    if (pendingAssessment) {
      launchAssessment(pendingAssessment, !resume);
      setPendingAssessment(null);
    }
  };

  const handleBackToDashboard = () => {
    setPremiumView('dashboard');
    setSelectedAssessment(null);
    window.history.back();
  };

  if (loading || checkingAuth || loadingSelected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <span className="text-lg">Loading assessment...</span>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return <Navigate to="/" />;
  }

  if (mode === 'free') {
    return <FreeAssessment assessment={assessment} />;
  }

  if (mode === 'premium') {
    if (!premiumAuthenticated) {
      return (
        <PremiumAuthPage
          assessment={assessment}
          onAuthSuccess={() => {
            setPremiumAuthenticated(true);
            window.history.pushState({ view: 'dashboard' }, '');
          }}
        />
      );
    }

    // Show premium assessment if selected
    if (premiumView === 'assessment' && selectedAssessment) {
      return (
          <PremiumAssessment
            assessment={selectedAssessment}
            onBack={handleBackToDashboard}
            startFresh={startFresh}
          />
        );
      }

      // Show premium dashboard
      return (
      <>
        <PremiumDashboard
          onStartAssessment={handleStartPremiumAssessment}
          onLogout={() => setPremiumAuthenticated(false)}
        />
        <PremiumResumeDialog
          open={resumeDialogOpen}
          onOpenChange={setResumeDialogOpen}
          assessmentTitle={pendingAssessment?.title || ''}
          onResume={() => handleResumeChoice(true)}
          onStartNew={() => handleResumeChoice(false)}
        />
      </>
    );
  }

  return <Navigate to="/" />;
};

export default AssessmentPortal;
